/**
 * Admin-facing port of packages/frontend/src/components/PlanUsageDisplay.tsx
 * Same data contract (PlanUsageInfo === GetPlanUsageOutput) and gauge logic,
 * adapted to a presentational component (no react-query / i18n dependency).
 */
import type {PlanUsageInfo} from '../lib/api-client';
import {formatDateTime} from '../utils/datetime';

interface ProgressBarProps {
  label: string;
  current: number;
  limit: number;
  unit: string;
  warningThreshold?: number;
  dangerThreshold?: number;
}

function ProgressBar({
  label,
  current,
  limit,
  unit,
  warningThreshold = 80,
  dangerThreshold = 90
}: ProgressBarProps) {
  const safeCurrent = current ?? 0;
  const safeLimit = limit ?? 0;

  // -1 means unlimited
  if (safeLimit === -1) {
    return (
      <div className='space-y-2'>
        <div className='flex items-center justify-between'>
          <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>{label}</span>
          <span className='text-sm text-gray-600 dark:text-gray-400'>
            {Math.round(safeCurrent)} {unit} / ∞
          </span>
        </div>
        <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700'>
          <div className='h-full rounded-full bg-blue-500' />
        </div>
        <p className='text-xs text-gray-500 dark:text-gray-400'>無制限</p>
      </div>
    );
  }

  const percentage = safeLimit > 0 ? (safeCurrent / safeLimit) * 100 : 0;
  const remaining = Math.max(0, safeLimit - safeCurrent);
  const safePercentage = Number.isFinite(percentage) ? percentage : 0;

  let barColor = 'bg-blue-500';
  let textColor = 'text-blue-600 dark:text-blue-400';

  if (safePercentage >= dangerThreshold) {
    barColor = 'bg-red-500';
    textColor = 'text-red-600 dark:text-red-400';
  } else if (safePercentage >= warningThreshold) {
    barColor = 'bg-yellow-500';
    textColor = 'text-yellow-600 dark:text-yellow-500';
  }

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>{label}</span>
        <span className={`text-sm font-medium ${textColor}`}>
          {Math.round(safeCurrent)} / {safeLimit} {unit}
        </span>
      </div>
      <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700'>
        <div
          className={`h-full rounded-full ${barColor} transition-all duration-300`}
          style={{ width: `${Math.min(100, safePercentage)}%` }}
        />
      </div>
      <div className='flex items-center justify-between text-xs'>
        <span className='text-gray-500 dark:text-gray-400'>
          {safePercentage >= dangerThreshold
            ? `${label} の制限に近づいています（${safePercentage.toFixed(0)}% 使用）`
            : safePercentage >= warningThreshold
              ? `${label} の制限の ${safePercentage.toFixed(0)}% を使用しています`
              : `残り ${Math.round(remaining)} ${unit}`}
        </span>
        <span className='text-gray-500 dark:text-gray-400'>{safePercentage.toFixed(0)}%</span>
      </div>
    </div>
  );
}

export function PlanUsageDisplay({
  planInfo,
  loading,
  error
}: {
  planInfo: PlanUsageInfo | null;
  loading: boolean;
  error?: string | null;
}) {
  if (loading) {
    return (
      <div className='rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800'>
        <div className='flex items-center space-x-3'>
          <div className='h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent' />
          <span className='text-gray-600 dark:text-gray-400'>プラン情報を読み込み中...</span>
        </div>
      </div>
    );
  }

  if (error || !planInfo?.currentPlan || !planInfo.limits || !planInfo.usage) {
    return (
      <div className='rounded-xl border border-red-200 bg-red-50 p-6 dark:border-red-800 dark:bg-red-900/20'>
        <p className='text-sm text-red-600 dark:text-red-400'>
          {error || 'プラン情報の読み込みに失敗しました'}
        </p>
      </div>
    );
  }

  const { currentPlan, limits, usage } = planInfo;
  const planDisplayName =
    currentPlan.displayName?.ja || currentPlan.displayName?.en || currentPlan.name;

  return (
    <div className='rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800'>
      <div className='mb-6 flex items-center justify-between'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>現在のプラン</h2>
          <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
            サブスクリプションと利用状況
          </p>
        </div>
        <div className='flex flex-col items-end gap-2'>
          <div className='rounded-lg bg-blue-100 px-4 py-2 dark:bg-blue-900/30'>
            <span className='text-lg font-bold text-blue-600 dark:text-blue-400'>
              {planDisplayName}
            </span>
          </div>
          <span className='text-xs text-gray-500 dark:text-gray-400'>tier: {currentPlan.tier}</span>
        </div>
      </div>

      {currentPlan.validUntil && (
        <div className='mb-6'>
          <p className='text-sm text-gray-600 dark:text-gray-400'>
            <span className='text-muted mr-3 font-bold'>有効期限</span>
            <span className='font-medium text-gray-900 dark:text-white'>
              {formatDateTime(currentPlan.validUntil, { dateStyle: 'long', timeStyle: 'medium' })}
            </span>
          </p>
        </div>
      )}

      <div className='rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'>
        <div className='rounded-t-lg bg-gray-50 p-4 dark:bg-gray-900/50'>
          <h3 className='text-sm font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400'>
            利用状況
          </h3>
        </div>

        <div className='border-t border-gray-200 p-4 dark:border-gray-700'>
          <div className='space-y-6'>
            {/* Pending Posts + Reposts
                同時予約枠（pendingPostsLimit）は予約投稿と予約リポストの合算で消費されるため、
                current にはリポスト分（pendingRepostsCount）を加算する。
                バックエンドが未対応で pendingRepostsCount が undefined の場合は
                従来どおり投稿数のみで表示する（防御的実装）。 */}
            <div>
              <ProgressBar
                label='保留中の予約（投稿+リポスト）'
                current={usage.pendingPostsCount + (usage.pendingRepostsCount ?? 0)}
                limit={limits.pendingPostsLimit}
                unit='件'
              />
              {usage.pendingRepostsCount !== undefined && (
                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                  内訳: 投稿 {usage.pendingPostsCount} 件 / リポスト {usage.pendingRepostsCount} 件
                </p>
              )}
            </div>

            {/* Monthly Posts Limit */}
            <ProgressBar
              label='期間内の予約投稿数（削除済み含む）'
              current={usage.monthlyPostsCount}
              limit={limits.monthlyPostsLimit}
              unit='件'
            />

            {/* Pending Delete Actions（予約削除は独立枠。期間内枠は消費しない） */}
            {usage.pendingDeleteActionsCount !== undefined && (
              <ProgressBar
                label='保留中の予約削除（独立枠）'
                current={usage.pendingDeleteActionsCount}
                limit={limits.pendingDeleteActionsLimit}
                unit='件'
              />
            )}
          </div>

          {/* Plan Limits Information */}
          <div className='my-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700'>
            <div className='flex flex-wrap items-center gap-x-12 gap-y-2'>
              <div className='flex flex-row gap-2'>
                <span className='text-sm font-semibold text-gray-700 dark:text-gray-300'>
                  最大予約日数
                </span>
                <span className='text-sm text-blue-600 dark:text-blue-400'>
                  {limits.maxScheduleDays === -1 ? '無制限' : `${limits.maxScheduleDays} 日`}
                </span>
              </div>
              <div className='flex flex-row gap-2'>
                <span className='text-sm font-semibold text-gray-700 dark:text-gray-300'>
                  スケジュール間隔
                </span>
                <span className='text-sm text-blue-600 dark:text-blue-400'>
                  {limits.scheduleIntervalMinutes} 分
                </span>
              </div>
              <div className='flex flex-row gap-2'>
                <span className='text-sm font-semibold text-gray-700 dark:text-gray-300'>
                  予約削除の同時上限
                </span>
                <span className='text-sm text-blue-600 dark:text-blue-400'>
                  {limits.pendingDeleteActionsLimit === -1
                    ? '無制限'
                    : `${limits.pendingDeleteActionsLimit} 件`}
                </span>
              </div>
              <div className='flex flex-row gap-2'>
                <span className='text-sm font-semibold text-gray-700 dark:text-gray-300'>
                  同一時刻あたりの予約アクション上限
                </span>
                <span className='text-sm text-blue-600 dark:text-blue-400'>
                  {limits.actionsPerSlotLimit === -1
                    ? '無制限'
                    : `${limits.actionsPerSlotLimit} 件`}
                </span>
              </div>
            </div>
          </div>

          <div className='my-1 text-right'>
            {usage.monthlyPeriodStart && usage.monthlyPeriodEnd && (
              <div className='pt-2 text-xs text-gray-500 dark:text-gray-400'>
                <span className='mr-1 font-medium'>集計期間:</span>
                <span>
                  {formatDateTime(usage.monthlyPeriodStart, { dateStyle: 'short' })} -{' '}
                  {formatDateTime(usage.monthlyPeriodEnd, { dateStyle: 'short' })}
                </span>
              </div>
            )}
            {usage.lastUpdated && (
              <div className='pt-2 text-xs text-gray-500 dark:text-gray-400'>
                <span className='mr-1 font-medium'>最終更新:</span>
                <span>
                  {formatDateTime(usage.lastUpdated, { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
